import React, { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { MAX_SPECIALIZATIONS, SPECIALIZATIONS } from "@shared/specializations";

/** Searchable multi-select of practice areas. `value` is a string[]; first entry is the primary. */
export function SpecializationPicker({ value = [], onChange, max = MAX_SPECIALIZATIONS, testid = "specialization-picker", placeholder = "Search practice areas…" }) {
  const [open, setOpen] = useState(false);
  const toggle = (s) => {
    if (value.includes(s)) return onChange(value.filter((v) => v !== s));
    if (value.length >= max) return;
    onChange([...value, s]);
  };
  return (
    <div data-testid={testid}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" role="combobox" aria-expanded={open} data-testid={`${testid}-trigger`}
                  className="mt-1 flex w-full h-10 items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 text-left text-sm hover:border-[#0A2540] focus:outline-none focus:border-[#0A2540]">
            <span className={value.length ? "text-[#0A2540] font-semibold" : "text-slate-400"}>
              {value.length ? `${value.length} selected · ${value[0]}${value.length > 1 ? ` +${value.length - 1}` : ""}` : "Select specializations"}
            </span>
            <ChevronsUpDown size={14} className="shrink-0 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]" align="start">
          <Command>
            <CommandInput placeholder={placeholder} data-testid={`${testid}-search`} />
            <CommandList>
              <CommandEmpty>No practice area found.</CommandEmpty>
              <CommandGroup>
                {SPECIALIZATIONS.map((s) => {
                  const on = value.includes(s);
                  const full = !on && value.length >= max;
                  return (
                    <CommandItem key={s} value={s} onSelect={() => toggle(s)} disabled={full} data-testid={`${testid}-option-${s.replace(/[^a-z]+/gi, "-").toLowerCase()}`} className="gap-2">
                      <span className={`grid h-4 w-4 place-items-center rounded border ${on ? "bg-[#0A2540] border-[#0A2540] text-white" : "border-slate-300"}`}>{on && <Check size={11} />}</span>
                      {s}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testid}-chips`}>
          {value.map((s, i) => (
            <span key={s} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${i === 0 ? "bg-[#0A2540] text-[#D4AF37]" : "bg-slate-100 text-[#0A2540]"}`} data-testid={`${testid}-chip-${i}`}>
              {s}
              <button type="button" aria-label={`Remove ${s}`} onClick={() => toggle(s)} className="rounded-full hover:bg-black/10 p-0.5"><X size={10} /></button>
            </span>
          ))}
        </div>
      )}
      <p className="mt-1 text-[11px] text-slate-400">Up to {max}. The first one is shown as your primary practice area.</p>
    </div>
  );
}
